import PasswordInput from './PasswordInput';
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getAttendanceConfig, setAttendanceConfig, getMyIp, getKioskAccounts, adminCreateKiosk,
  adminSetEmployeePin, adminEmployeesWithPin, DEFAULT_ATT,
  adminEmployeeOverrides, describeOverride,
} from '../lib/attendance';
import { getAllProfiles } from '../lib/supabase';
import { IconTile, Glyph } from './Icons';
import { hasFeature } from '../lib/plans';

const field = 'mt-1 w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white';

const Toggle = ({ on, onChange, label, hint }) => (
  <label className="flex items-start gap-3 cursor-pointer">
    <button type="button" role="switch" aria-checked={on} onClick={() => onChange(!on)}
      className={`mt-0.5 shrink-0 w-10 h-6 rounded-full transition-colors relative ${on ? 'bg-sky-600' : 'bg-gray-200'}`}>
      <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${on ? 'left-[18px]' : 'left-0.5'}`} />
    </button>
    <span className="text-sm text-gray-800">{label}{hint && <span className="block text-xs text-gray-400 mt-0.5">{hint}</span>}</span>
  </label>
);

const Card = ({ icon, title, desc, on: onRaw, onToggle: onToggleRaw, locked, children }) => {
  const on = onRaw && !locked; const onToggle = locked ? () => {} : onToggleRaw;
  return (
  <div className={`rounded-2xl border p-4 ${locked ? 'order-3 border-gray-200 bg-gray-50 opacity-60 grayscale pointer-events-none select-none' : on ? 'border-sky-200 bg-sky-50/40' : 'border-gray-100'}`} aria-disabled={locked || undefined}>
    <div className="flex items-start gap-3">
      <IconTile name={icon} size="sm" />
      <div className="flex-1 min-w-0"><p className="font-semibold text-gray-900 text-sm">{title}{locked && <span className="ml-2 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-100 px-2 py-0.5 rounded-full">Disponible en un plan superior</span>}</p><p className="text-xs text-gray-400 mt-0.5">{desc}</p></div>
      <button type="button" role="switch" aria-checked={on} onClick={() => onToggle(!on)}
        className={`shrink-0 w-11 h-6 rounded-full transition-colors relative ${on ? 'bg-sky-600' : 'bg-gray-200'}`}>
        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${on ? 'left-[22px]' : 'left-0.5'}`} />
      </button>
    </div>
    {on && children && <div className="mt-4 pl-9 space-y-3">{children}</div>}
  </div>
  );
};

// Cada organización elige cómo marcan entrada y salida sus empleados.
export default function AttendanceSettings({ showToast, canEmployees = true, features }) {
  const inc = (k) => hasFeature(features, k);
  const [cfg, setCfg] = useState(DEFAULT_ATT);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ips, setIps] = useState('');
  const [kiosks, setKiosks] = useState([]);
  const [emps, setEmps] = useState([]);
  const [withPin, setWithPin] = useState([]);
  const [newK, setNewK] = useState({ name: '', email: '', password: '' });
  const [overrides, setOverrides] = useState({});
  const [shown, setShown] = useState({}); // id -> código recién generado
  const toastRef = useRef(showToast);
  toastRef.current = showToast;

  const reload = useCallback(async () => {
    try {
      const c = await getAttendanceConfig();
      setCfg(c); setIps((c.network.allowed_ips || []).join('\n'));
      setKiosks(await getKioskAccounts());
      if (canEmployees) {
        const all = await getAllProfiles();
        setEmps((all || []).filter((p) => p.role === 'employee'));
        setWithPin(await adminEmployeesWithPin());
        setOverrides(await adminEmployeeOverrides());
      }
    } catch (e) { toastRef.current?.(e.message || 'No se pudo cargar la configuración de registro', 'error'); }
    setLoaded(true);
  }, [canEmployees]);
  useEffect(() => { reload(); }, [reload]);

  const set = (path, v) => setCfg((c) => {
    const n = JSON.parse(JSON.stringify(c)); let o = n;
    path.slice(0, -1).forEach((k) => { o = o[k]; }); o[path[path.length - 1]] = v; return n;
  });

  const save = async () => {
    setSaving(true);
    try {
      const list = ips.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
      const next = { ...cfg, network: { ...cfg.network, allowed_ips: list } };
      if (!next.methods.phone && !next.methods.qr_screen && !next.methods.kiosk) throw new Error('Dejá al menos un modo activo. Si no, nadie podrá marcar (el registro manual del administrador siempre queda disponible).');
      await setAttendanceConfig(next);
      showToast?.('Modos de registro actualizados');
      await reload();
    } catch (e) { showToast?.(e.message || 'No se pudo guardar', 'error'); }
    setSaving(false);
  };

  const useMyNet = async () => {
    try {
      const ip = await getMyIp();
      if (!ip) throw new Error('No pudimos detectar tu red');
      setIps((v) => (v.split(/\s+/).includes(ip) ? v : (v ? `${v}\n${ip}` : ip)));
      showToast?.(`Tu IP actual es ${ip}. Revisá que sea la del local y guardá.`);
    } catch (e) { showToast?.(e.message, 'error'); }
  };

  const createKiosk = async () => {
    try {
      await adminCreateKiosk(newK.name.trim(), newK.email.trim(), newK.password);
      setNewK({ name: '', email: '', password: '' });
      showToast?.('Cuenta de kiosco creada. Iniciá sesión con ella en la tablet.');
      await reload();
    } catch (e) { showToast?.(e.message || 'No se pudo crear', 'error'); }
  };

  const genPin = async (id) => {
    try { const pin = await adminSetEmployeePin(id); setShown((s) => ({ ...s, [id]: pin })); setWithPin((w) => (w.includes(id) ? w : [...w, id])); }
    catch (e) { showToast?.(e.message || 'No se pudo generar el código', 'error'); }
  };
  const genMissing = async () => {
    for (const e of emps.filter((x) => !withPin.includes(x.id))) { await genPin(e.id); }
  };
  const printSheet = () => {
    const rows = emps.filter((e) => shown[e.id]).map((e) => `<tr><td>${e.name}</td><td style="font:700 20px monospace;letter-spacing:3px">${shown[e.id]}</td></tr>`).join('');
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<html><head><title>Códigos de kiosco</title><style>body{font-family:sans-serif;padding:24px}td{padding:8px 16px;border-bottom:1px solid #ddd}</style></head><body><h2>Códigos personales de kiosco</h2><p>Entregá cada código en privado. No se vuelve a mostrar.</p><table>${rows}</table></body></html>`);
    w.document.close(); w.print();
  };

  if (!loaded) return <div className="bg-white rounded-3xl border border-gray-100 p-5 text-sm text-gray-400">Cargando modos de registro…</div>;
  const m = cfg.methods;

  return (
    <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-5 flex flex-col gap-4">
      <div>
        <h3 className="font-bold text-gray-900 text-sm">Cómo registran entrada y salida tus empleados</h3>
        <p className="text-xs text-gray-400 mt-0.5">Podés activar varios modos a la vez. El registro manual del administrador siempre está disponible.</p>
      </div>

      {(!inc('method_qr') || !inc('method_kiosk') || !inc('network_restrict')) && (
        <div className="order-2 pt-2 border-t border-gray-100">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Podés sumarlo con un plan superior</p>
          <p className="text-xs text-gray-400 mt-0.5">Estas opciones no están en tu plan actual y no se pueden modificar.</p>
        </div>
      )}

      <Card icon="phone" title="Desde el celular de cada empleado" desc="Botón de entrada/salida en la app, con ubicación y huella / Face ID." on={m.phone} onToggle={(v) => set(['methods', 'phone'], v)}>
        <Toggle on={cfg.phone.require_location} onChange={(v) => set(['phone', 'require_location'], v)} label="Pedir ubicación GPS" hint="Guarda dónde marcó. Se compara con las sedes configuradas." />
        <Toggle on={cfg.phone.require_biometric} onChange={(v) => set(['phone', 'require_biometric'], v)} label="Pedir huella / Face ID" hint={cfg.phone.biometric_server ? 'Se verifica en el servidor.' : 'Se verifica solo en el celular; no es una prueba en el servidor.'} />
        {cfg.phone.require_biometric && (
          <Toggle on={!!cfg.phone.biometric_server && inc('biometric_server')} onChange={(v) => inc('biometric_server') ? set(['phone', 'biometric_server'], v) : showToast?.('La verificación en el servidor no está incluida en tu plan', 'error')} label="Verificación segura en el servidor (recomendado)" hint="El servidor comprueba una firma criptográfica del dispositivo en cada registro. Cada empleado vuelve a registrar su huella una vez, y ya no hay alternativa si la huella falla (usá el registro manual o el kiosco). Solo en el dominio principal de Gestión360." />
        )}
      </Card>

      <Card icon="qr" title="QR en pantalla" desc="Una pantalla en el local muestra un QR que cambia solo; el empleado lo escanea." locked={!inc('method_qr')} on={m.qr_screen} onToggle={(v) => set(['methods', 'qr_screen'], v)}>
        <label className="block text-xs text-gray-500">Renovar el código cada
          <select className={field} value={cfg.qr.rotate_seconds} onChange={(e) => set(['qr', 'rotate_seconds'], Number(e.target.value))}>
            {[30, 60, 120, 300].map((s) => <option key={s} value={s}>{s < 60 ? `${s} segundos` : `${s / 60} minuto${s > 60 ? 's' : ''}`}</option>)}
          </select>
        </label>
        <Toggle on={cfg.qr.require_location} onChange={(v) => set(['qr', 'require_location'], v)} label="Pedir también la ubicación" hint="Evita que alguien reenvíe una foto del QR desde otro lugar." />
        <button type="button" onClick={() => window.open('/pantalla', '_blank')} className="text-sm font-semibold text-sky-700 bg-sky-100 px-3 py-2 rounded-xl">Abrir pantalla del QR</button>
        <p className="text-xs text-gray-400">Abrila en un monitor o tablet del local con tu sesión de administrador, o dejala dentro de una cuenta de kiosco.</p>
      </Card>

      <Card icon="keypad" title="Kiosco con código personal" desc="Una tablet fija en la entrada. Cada empleado ingresa su código de 6 dígitos." locked={!inc('method_kiosk')} on={m.kiosk} onToggle={(v) => set(['methods', 'kiosk'], v)}>
        <div>
          <p className="text-xs font-semibold text-gray-600">Cuentas de kiosco ({kiosks.length}/10)</p>
          {kiosks.map((k) => <p key={k.id} className="text-xs text-gray-500 mt-1">• {k.name} · {k.email}</p>)}
          <div className="grid gap-2 mt-2 sm:grid-cols-3">
            <input className={field} placeholder="Nombre (ej. Entrada)" value={newK.name} onChange={(e) => setNewK({ ...newK, name: e.target.value })} />
            <input className={field} placeholder="Email de la tablet" value={newK.email} onChange={(e) => setNewK({ ...newK, email: e.target.value })} />
            <PasswordInput className={field} placeholder="Contraseña (8+)" value={newK.password} onChange={(e) => setNewK({ ...newK, password: e.target.value })} />
          </div>
          <button type="button" onClick={createKiosk} disabled={!newK.name || !newK.email || newK.password.length < 8} className="mt-2 text-sm font-semibold text-white bg-sky-600 px-3 py-2 rounded-xl disabled:opacity-40">Crear cuenta de kiosco</button>
        </div>
        {canEmployees && (
          <div>
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-semibold text-gray-600">Códigos de empleados</p>
              <div className="flex gap-2">
                <button type="button" onClick={genMissing} className="text-xs font-semibold text-sky-700 bg-sky-100 px-2.5 py-1.5 rounded-lg">Generar los que faltan</button>
                {Object.keys(shown).length > 0 && <button type="button" onClick={printSheet} className="text-xs font-semibold text-gray-700 bg-gray-100 px-2.5 py-1.5 rounded-lg">Imprimir</button>}
              </div>
            </div>
            <div className="mt-2 max-h-64 overflow-y-auto divide-y divide-gray-100 border border-gray-100 rounded-xl">
              {emps.map((e) => (
                <div key={e.id} className="flex items-center justify-between gap-2 px-3 py-2">
                  <span className="text-sm text-gray-800 truncate">{e.name}</span>
                  <span className="flex items-center gap-2 shrink-0">
                    {shown[e.id] ? <b className="font-mono tracking-widest text-sky-700">{shown[e.id]}</b> : <span className="text-xs text-gray-400">{withPin.includes(e.id) ? 'Con código' : 'Sin código'}</span>}
                    <button type="button" onClick={() => genPin(e.id)} className="text-xs text-sky-700 font-semibold">{withPin.includes(e.id) ? 'Regenerar' : 'Generar'}</button>
                  </span>
                </div>
              ))}
              {emps.length === 0 && <p className="text-xs text-gray-400 p-3">Todavía no hay empleados.</p>}
            </div>
            <p className="text-xs text-gray-400 mt-1">El código se muestra una sola vez; en el servidor se guarda cifrado. Si alguien lo olvida, regeneralo.</p>
          </div>
        )}
      </Card>

      <div className={`rounded-2xl border p-4 ${inc('network_restrict') ? 'border-gray-100' : 'order-3 border-gray-200 bg-gray-50 opacity-60 grayscale pointer-events-none select-none'}`}>
        <Toggle on={cfg.network.restrict && inc('network_restrict')} onChange={(v) => (v && !inc('network_restrict')) ? showToast?.('La restricción por red no está incluida en tu plan', 'error') : set(['network', 'restrict'], v)} label={<span className="inline-flex items-center gap-1.5"><Glyph name="wifi" className="w-4 h-4 text-sky-700" />Solo desde la red del local</span>} hint="Si está activo, nadie puede marcar fuera de las IP indicadas (celular, QR o kiosco)." />
        {cfg.network.restrict && (
          <div className="mt-3 pl-1">
            <textarea className={`${field} font-mono h-24`} placeholder={'Una por línea. Ej:\n190.12.34.56\n190.12.34.0/24'} value={ips} onChange={(e) => setIps(e.target.value)} />
            <button type="button" onClick={useMyNet} className="mt-2 text-xs font-semibold text-sky-700 bg-sky-100 px-3 py-2 rounded-xl">Usar mi red actual</button>
            <p className="text-xs text-gray-400 mt-1">Probalo antes: conectate al wifi del local, tocá el botón y comprobá que la IP detectada es la correcta. Si tus empleados marcan con datos móviles quedarían bloqueados.</p>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-gray-100 p-4">
        <Toggle on={cfg.window.enabled} onChange={(v) => set(['window', 'enabled'], v)}
          label={<span className="inline-flex items-center gap-1.5"><Glyph name="clock" className="w-4 h-4 text-sky-700" />Horario habilitado para registrar la entrada</span>}
          hint="Se calcula desde el horario de cada persona (y sus excepciones). Fuera de ese margen el sistema NO deja registrar la entrada y le muestra el motivo y el horario permitido. La salida nunca se bloquea. Si trabajó igual, el administrador puede cargar la asistencia a mano. Para permitir marcar a cualquier hora y solo recibir un aviso, apagá esta opción y activá el aviso en Reglas." />
        {cfg.window.enabled && (
          <div className="mt-3 pl-1 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs font-semibold text-gray-500">Desde cuántos minutos antes de entrar
                <input type="number" min="0" max="240" step="5" className={field} value={cfg.window.before_min} onChange={(e) => set(['window', 'before_min'], Number(e.target.value))} />
              </label>
              <label className="text-xs font-semibold text-gray-500">Hasta cuántos minutos después de salir
                <input type="number" min="0" max="720" step="15" className={field} value={cfg.window.after_min} onChange={(e) => set(['window', 'after_min'], Number(e.target.value))} />
              </label>
            </div>
            <Toggle on={cfg.window.block_non_working} onChange={(v) => set(['window', 'block_non_working'], v)}
              label="Bloquear también los días sin turno" hint="Si está apagado, en un día sin horario puede marcar y queda señalado como fuera de horario." />
            <p className="text-xs text-sky-700 bg-sky-50 rounded-xl px-3 py-2">Ejemplo: con turno de 09:00 a 17:00, se puede marcar la entrada entre las {(() => { const m = 540 - (cfg.window.before_min || 0); const e = 1020 + (cfg.window.after_min || 0); const f = (x) => { const v = ((x % 1440) + 1440) % 1440; return String(Math.floor(v / 60)).padStart(2, '0') + ':' + String(v % 60).padStart(2, '0'); }; return f(m) + ' y las ' + f(e); })()}. Fuera de ese rango se rechaza.</p>
            <p className="text-xs text-gray-400">El registro manual del administrador y el cierre de jornada no se ven afectados.</p>
          </div>
        )}
      </div>

      {canEmployees && (
        <div className="rounded-2xl border border-gray-100 p-4">
          <p className="font-semibold text-gray-900 text-sm inline-flex items-center gap-1.5"><Glyph name="user" className="w-4 h-4 text-sky-700" />Excepciones por empleado</p>
          <p className="text-xs text-gray-400 mt-0.5">Quién tiene un modo distinto. Para cambiarlo, abrí la ficha de la persona (Empleados → Ficha → Cómo marca).</p>
          <div className="mt-3 max-h-64 overflow-y-auto divide-y divide-gray-100 border border-gray-100 rounded-xl">
            {emps.map((e) => (
              <div key={e.id} className="px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-gray-800 truncate">{e.name}</span>
                  <span className="flex items-center gap-2 shrink-0">
                    <span className={`text-xs ${overrides[e.id] ? 'text-sky-700 font-semibold' : 'text-gray-400'}`}>{describeOverride(overrides[e.id])}</span>

                  </span>
                </div>
              </div>
            ))}
            {emps.length === 0 && <p className="text-xs text-gray-400 p-3">Todavía no hay empleados.</p>}
          </div>
        </div>
      )}

      <button type="button" onClick={save} disabled={saving} className="w-full text-sm font-semibold text-white bg-sky-600 py-3 rounded-2xl disabled:opacity-50">{saving ? 'Guardando…' : 'Guardar modos de registro'}</button>
    </div>
  );
}
